import { Injectable, inject } from '@angular/core';
import { IndexedDbStorageService } from './indexed-db-storage.service';

export type CameraChannelMessage =
  | { type: 'photo-saved' }
  | { type: 'form-heartbeat' }
  | { type: 'form-closing' };

export interface CameraChannel {
  post(message: CameraChannelMessage): void;
  subscribe(callback: (message: CameraChannelMessage) => void): () => void;
  close(): void;
}

@Injectable({ providedIn: 'root' })
export class CameraChannelService {
  private readonly storage = inject(IndexedDbStorageService);

  create(jobId: number | string): CameraChannel {
    const channelName = `tops-camera-${jobId}`;
    const storageKey = `tops-camera-msg-${jobId}`;
    const storage = this.storage;
    const listeners = new Set<(message: CameraChannelMessage) => void>();
    let broadcast: BroadcastChannel | null = null;
    let storageHandler: ((event: StorageEvent) => void) | null = null;
    let messageSequence = 0;

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        broadcast = new BroadcastChannel(channelName);
        broadcast.addEventListener('message', (event: MessageEvent<CameraChannelMessage>) => {
          if (event.data?.type) listeners.forEach((listener) => listener(event.data));
        });
      } catch {
        broadcast = null;
      }
    }

    if (!broadcast) {
      storageHandler = (event: StorageEvent) => {
        if (event.key !== storageKey || !event.newValue) return;
        try {
          const parsed = JSON.parse(event.newValue) as { message: CameraChannelMessage };
          if (parsed.message?.type) listeners.forEach((listener) => listener(parsed.message));
        } catch {}
      };
      window.addEventListener('storage', storageHandler);
    }

    return {
      post(message) {
        if (broadcast) {
          try {
            broadcast.postMessage(message);
            return;
          } catch {}
        }
        try {
          messageSequence += 1;
          storage.setItem(storageKey, JSON.stringify({ message, sequence: messageSequence }));
        } catch {}
      },
      subscribe(callback) {
        listeners.add(callback);
        return () => listeners.delete(callback);
      },
      close() {
        listeners.clear();
        if (broadcast) broadcast.close();
        if (storageHandler) window.removeEventListener('storage', storageHandler);
      },
    };
  }
}
